import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { observer } from "mobx-react";
import useStore from "../../../../helpers/useStore";
import { DoctorContext } from "../../../../stores/Doctor/doctor.provider";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ContactInfo {
  salutation?: string;
  firstNames?: string;
  lastName?: string;
  businessName?: string | null;
  addressLine1?: string | null;
  city?: string | null;
  zip?: string | null;
  phone1?: string | null;
}

interface ClinicInfo {
  id: string;
  BSNR?: string | null;
  contactInfo?: ContactInfo;
}

interface DoctorInfo {
  id: string;
  LANR?: string | null;
  contactInfo?: ContactInfo;
  clinics?: ClinicInfo[];
}

export interface DoctorClinicOption {
  doctor: DoctorInfo;
  clinic: ClinicInfo;
  label: string;
  isAddNew?: boolean;
}

/* Sentinel option for "Add new" */
const ADD_NEW_OPTION: DoctorClinicOption = {
  doctor: { id: "__add_new__" } as DoctorInfo,
  clinic: { id: "__add_new__" } as ClinicInfo,
  label: "+ Neuen Arzt / Praxis anlegen",
  isAddNew: true,
};

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface DoctorClinicAutocompleteProps {
  /** Called when the user picks an existing doctor+clinic pair */
  onSelect: (doctor: DoctorInfo, clinic: ClinicInfo) => void;
  /** Called when the user clicks "+ Neuen Arzt / Praxis anlegen", receives current search text */
  onAddNew: (searchTerm: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Pre-fills the search field on mount (e.g. with the currently linked doctor) */
  initialInputValue?: string;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                             */
/* ------------------------------------------------------------------ */

const DEBOUNCE_MS = 300;

function buildLabel(doctor: DoctorInfo, clinic: ClinicInfo): string {
  const ci = doctor.contactInfo;
  const cliCi = clinic.contactInfo;

  const doctorName = ci
    ? `${ci.lastName || ""}${ci.firstNames ? ", " + ci.firstNames : ""} ${ci.salutation || ""}`.trim()
    : doctor.LANR ? `LANR: ${doctor.LANR}` : "Unbekannter Arzt";

  const clinicName = cliCi?.businessName || "";
  const clinicCity = cliCi?.city || "";

  return [doctorName, clinicName, clinicCity].filter(Boolean).join(" • ");
}

/**
 * Flatten the paginated doctor list into one option per doctor-clinic pair.
 * Doctors without clinics still appear once with a placeholder clinic.
 */
function flattenResults(doctors: DoctorInfo[]): DoctorClinicOption[] {
  const options: DoctorClinicOption[] = [];

  for (const doctor of doctors) {
    const clinics = doctor.clinics?.length ? doctor.clinics : [{ id: "", contactInfo: {} } as ClinicInfo];
    for (const clinic of clinics) {
      options.push({
        doctor,
        clinic,
        label: buildLabel(doctor, clinic),
      });
    }
  }

  return options;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const DoctorClinicAutocomplete: React.FC<DoctorClinicAutocompleteProps> = observer(
  ({ onSelect, onAddNew, placeholder = "Arzt / Praxis suchen…", initialInputValue = "" }) => {
    const doctorStore = useStore(DoctorContext);

    const [input, setInput] = useState(initialInputValue);
    const [options, setOptions] = useState<DoctorClinicOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedValue, setSelectedValue] = useState<DoctorClinicOption | null>(null);

    const inflightRef = useRef<AbortController | null>(null);

    /* ---- fetch with debounce ---- */
    const fetchSuggestions = useCallback(
      async (query: string) => {
        if (!query?.trim()) {
          setOptions([]);
          return;
        }

        inflightRef.current?.abort();
        const controller = new AbortController();
        inflightRef.current = controller;

        setLoading(true);
        try {
          const result = await doctorStore.searchDoctor(1, 20, { searchTerm: query });
          if (controller.signal.aborted) return;

          const doctors: DoctorInfo[] = result?.data ?? result ?? [];
          const flat = flattenResults(Array.isArray(doctors) ? doctors : []);
          setOptions([...flat, ADD_NEW_OPTION]);
        } catch (err: any) {
          if (err?.name !== "AbortError") console.error("Doctor search error:", err);
        } finally {
          if (!controller.signal.aborted) setLoading(false);
          inflightRef.current = null;
        }
      },
      [doctorStore]
    );

    const debouncedFetch = useMemo(() => {
      let timer: ReturnType<typeof setTimeout>;
      return (q: string) => {
        clearTimeout(timer);
        timer = setTimeout(() => fetchSuggestions(q), DEBOUNCE_MS);
      };
    }, [fetchSuggestions]);

    useEffect(() => {
      debouncedFetch(input);
    }, [input, debouncedFetch]);

    /* ---- selection handler ---- */
    function handleSelect(_: any, value: DoctorClinicOption | null) {
      if (!value) return;
      if (value.isAddNew) {
        onAddNew(input);
      } else {
        onSelect(value.doctor, value.clinic);
      }
      // Reset
      setSelectedValue(null);
      setInput("");
      setOptions([]);
    }

    /* ---- render ---- */
    return (
      <Autocomplete<DoctorClinicOption, false, false, false>
        value={selectedValue}
        inputValue={input}
        options={options}
        loading={loading}
        filterOptions={(x) => x} // Disable client-side filtering — server handles it
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(o, v) =>
          o.doctor.id === v.doctor.id && o.clinic.id === v.clinic.id
        }
        onChange={handleSelect}
        onInputChange={(_, v, reason) => {
          setInput(v);
          if (reason === "clear") {
            setSelectedValue(null);
            setOptions([]);
          }
        }}
        noOptionsText="Keine Ergebnisse"
        loadingText="Suche…"
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            size="small"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{ width: "100%" }}
          />
        )}
        renderOption={(props, option) => {
          if (option.isAddNew) {
            return (
              <React.Fragment key="add-new">
                <Divider />
                <li {...props} key="add-new-option">
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontWeight: 600,
                      py: 0.5,
                      backgroundColor: "#e3f2fd",
                      width: "100%",
                      borderRadius: 1,
                      px: 1,
                    }}
                  >
                    <AddIcon fontSize="small" sx={{ color: "#1565c0" }} />
                    <Typography variant="body2" sx={{ fontWeight: 600, color: "#1565c0" }}>
                      Neuen Arzt / Praxis anlegen
                    </Typography>
                  </Box>
                </li>
              </React.Fragment>
            );
          }

          const ci = option.doctor.contactInfo;
          const cliCi = option.clinic.contactInfo;
          const lanr = option.doctor.LANR || "–";
          const bsnr = option.clinic.BSNR || "–";

          return (
            <li {...props} key={`${option.doctor.id}-${option.clinic.id}`}>
              <Box sx={{ display: "flex", flexDirection: "column", py: 0.5, width: "100%" }}>
                {/* Doctor line */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {ci?.salutation || ""} {ci?.firstNames || ""} {ci?.lastName || ""}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", ml: 2, whiteSpace: "nowrap" }}>
                    LANR: {lanr}
                  </Typography>
                </Box>
                {/* Clinic line */}
                {(cliCi?.businessName || option.clinic.id) && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography variant="caption" color="text.secondary">
                      {cliCi?.businessName || "Praxis"}
                      {cliCi?.addressLine1 ? ` — ${cliCi.addressLine1}` : ""}
                      {cliCi?.zip ? `, ${cliCi.zip}` : ""}
                      {cliCi?.city ? ` ${cliCi.city}` : ""}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary", ml: 2, whiteSpace: "nowrap" }}>
                      BSNR: {bsnr}
                    </Typography>
                  </Box>
                )}
              </Box>
            </li>
          );
        }}
        sx={{ mb: 1 }}
      />
    );
  }
);

export default DoctorClinicAutocomplete;
